import { supabase } from './supabase'

/**
 * What the family has sent recently, as the admin sees it.
 *
 * docs/spec.md §7: Incoming is a VIEW, not a state. Nothing is ever stuck
 * here — an item lives in Payments from the moment it is processed, and this
 * list is only a lens over "arrived recently". So ignoring it for a week
 * cannot cause a missed deadline.
 */

export type IncomingStatus = 'ready' | 'review' | 'needs_you' | 'processing'

export interface IncomingItem {
  documentId: string
  sentBy: string
  sentAt: string
  status: IncomingStatus
  /** Short reason, in Dutch, shown under the title. */
  note: string | null
  supplier: string | null
  amountCents: number | null
  dueDate: string | null
  obligationId: string | null
  lane: string | null
}

export async function loadIncoming(limit = 20): Promise<IncomingItem[]> {
  // Deliberately more than `limit`: most of what comes back is filtered out
  // below, and fetching exactly the page size would leave the section looking
  // empty on a family with a long history.
  const { data: docs, error } = await supabase
    .from('documents')
    .select('id, uploaded_at, uploaded_by, state, failure_reason')
    .is('deleted_at', null)
    .order('uploaded_at', { ascending: false })
    .limit(limit * 4)
  if (error) throw error
  if (!docs || docs.length === 0) return []

  const ids = docs.map((d) => d.id as string)

  // Latest extraction per document. Append-only means several may exist; the
  // newest is the current reading.
  const { data: extractions } = await supabase
    .from('extractions')
    .select('document_id, lane, lane_reasons, created_at, produced_by')
    .in('document_id', ids)
    .order('created_at', { ascending: false })

  const latest = new Map<string, { lane: string | null; reasons: string[] }>()
  // A human extraction is the admin saying "I have dealt with this" — it is
  // written by nothing else. That makes it the acknowledgement §7 asks for.
  const acknowledged = new Set<string>()
  for (const e of extractions ?? []) {
    const key = e.document_id as string
    if (!latest.has(key)) {
      latest.set(key, { lane: e.lane as string | null, reasons: (e.lane_reasons ?? []) as string[] })
    }
    if (e.produced_by === 'human') acknowledged.add(key)
  }

  const { data: obligations } = await supabase
    .from('obligations')
    .select('id, source_document_id, description, total_amount_cents, due_date')
    .in('source_document_id', ids)
    .is('deleted_at', null)

  const byDocument = new Map((obligations ?? []).map((o) => [o.source_document_id as string, o]))

  const { data: people } = await supabase.from('users').select('id, display_name')
  const names = new Map((people ?? []).map((p) => [p.id as string, p.display_name as string]))

  const rows = docs.map((d) => {
    const extraction = latest.get(d.id as string)
    const obligation = byDocument.get(d.id as string)
    const { status, note } = classify(
      d.state as string,
      d.failure_reason as string | null,
      extraction?.lane ?? null,
      extraction?.reasons ?? [],
      d.uploaded_at as string,
    )

    return {
      documentId: d.id as string,
      sentBy: names.get(d.uploaded_by as string) ?? 'Familie',
      sentAt: d.uploaded_at as string,
      status,
      note,
      supplier: (obligation?.description as string | undefined) ?? null,
      amountCents: obligation ? Number(obligation.total_amount_cents) : null,
      dueDate: (obligation?.due_date as string | undefined) ?? null,
      obligationId: (obligation?.id as string | undefined) ?? null,
      lane: extraction?.lane ?? null,
    }
  })

  return rows
    .filter((item) => isIncoming(item, acknowledged.has(item.documentId)))
    .slice(0, limit)
}

/**
 * Whether an item still belongs in "Nieuw van familie".
 *
 * §7: Incoming is a view, not a state — "a filtered lens over arrived recently
 * and not yet acknowledged by the admin". There was no lens. Every document
 * ever sent stayed in the section for as long as it was among the twenty
 * newest, so an invoice verified and paid in August was still sitting under
 * "Nieuw van familie" in September, wearing a green Klaar chip. Once a family
 * has twenty documents the genuinely new ones are buried among the settled
 * ones, which is the exact opposite of an inbox.
 *
 * Two ways out, both from §7:
 *
 *   * Acknowledged. A human extraction exists, which is written only when
 *     somebody pressed "Klopt" or corrected a field. Nothing else writes one.
 *
 *   * Ready and old. A green-lane document needs nothing from anybody; it is
 *     shown so you know it arrived, and then it clears itself after 48 hours.
 *     Items that need something — review, needs_you, still processing — never
 *     clear on a timer, because a timer would hide work.
 *
 * Nothing is lost either way: the obligation lives in Payments and the letter
 * in Letters from the moment it is processed, and due-date reminders do not
 * come from here. The inbox is a convenience, never a holding pen.
 */
export const READY_CLEARS_AFTER_HOURS = 48

export function isIncoming(
  item: Pick<IncomingItem, 'status' | 'sentAt'>,
  acknowledged: boolean,
  now = new Date(),
): boolean {
  if (acknowledged) return false
  if (item.status !== 'ready') return true

  const ageHours = (now.getTime() - new Date(item.sentAt).getTime()) / 3_600_000
  return ageHours < READY_CLEARS_AFTER_HOURS
}

/**
 * How long "wordt gelezen" may honestly be shown.
 *
 * The worker ticks every minute and a document takes well under one. Fifteen
 * minutes is far past any legitimate delay — a queued job, a cold start, a
 * retry with backoff — while still being short enough that nobody spends an
 * evening waiting on a worker that stopped days ago.
 */
export const STUCK_AFTER_MINUTES = 15

export function isStuck(uploadedAt: string, now = new Date()): boolean {
  const age = now.getTime() - new Date(uploadedAt).getTime()
  return age > STUCK_AFTER_MINUTES * 60_000
}

/**
 * Three states, and only three (spec §7). The wording is what the admin reads,
 * so it says what to do rather than naming an internal lane.
 */
function classify(
  state: string,
  failureReason: string | null,
  lane: string | null,
  reasons: string[],
  uploadedAt: string,
  now = new Date(),
): { status: IncomingStatus; note: string | null } {
  if (state === 'failed') {
    // The dead-letter case. Never a raw error string — the admin gets a
    // sentence they can act on, and the technical reason stays in the job row.
    return { status: 'needs_you', note: failureReason ? 'Ik kon dit niet goed lezen' : 'Kon dit niet verwerken' }
  }
  if (state === 'pending' || state === 'processing') {
    // A spinner that never stops is a lie, and the longer it spins the bigger
    // the lie. Extraction takes under a minute; anything past the threshold
    // means the worker is not running at all, and the honest thing is to say
    // so and offer the way round it rather than keep reassuring someone for
    // two days. Rule 18: a document is never stuck with no way out.
    if (isStuck(uploadedAt, now)) {
      return { status: 'needs_you', note: 'Dit duurt te lang — zelf invullen?' }
    }
    return { status: 'processing', note: 'Wordt gelezen…' }
  }

  if (lane === 'red') {
    if (reasons.includes('iban_changed_for_known_supplier')) {
      return { status: 'needs_you', note: 'Rekeningnummer is veranderd' }
    }
    if (reasons.includes('near_certain_duplicate')) {
      return { status: 'needs_you', note: 'Waarschijnlijk dubbel' }
    }
    return { status: 'needs_you', note: 'Rekeningnummer klopt niet' }
  }

  if (lane === 'amber') {
    if (reasons.includes('unknown_supplier')) return { status: 'review', note: 'Nieuwe leverancier' }
    if (reasons.includes('amount_outside_supplier_band')) {
      return { status: 'review', note: 'Ongewoon bedrag' }
    }
    if (reasons.includes('possible_duplicate')) return { status: 'review', note: 'Mogelijk dubbel' }
    return { status: 'review', note: 'Even nakijken' }
  }

  if (lane === 'blue') return { status: 'review', note: 'Kort bevestigen' }

  return { status: 'ready', note: null }
}

export function formatEuro(cents: number | null): string {
  if (cents === null) return ''
  return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(cents / 100)
}

export function formatDueDate(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(`${iso}T00:00:00`)
  return new Intl.DateTimeFormat('nl-BE', { day: 'numeric', month: 'short' }).format(d)
}
